import { client } from "@/client";
import { toast } from "react-toastify";
import { useTranslation } from "react-i18next";
import { useParams } from "react-router-dom";
import { CustomToastComponent } from "@/components/shared";
import { useMutation } from "@apollo/client/react";
import {
  AddMobileServiceMutation,
  AddMobileServiceMutationVariables,
  MobileService,
  AddMobileServiceDocument,
  FetchTenderByIdDocument,
  FetchServicesOnTenderDocument,
} from "@/graphql/__generated__/graphql";

/**
 * Custom hook for adding a service to a tender and handling the necessary cache updates.
 * @param {Object} options - Optional configuration options
 * @param {boolean} [options.disableToast=false] - Whether to disable the success toast
 * @param {boolean} [options.disableRefetch=false] - Whether to disable refetching queries
 * @returns {Object} - Contains the addMobileServiceMutation function, loading state, and error state.
 */
export default function useAddMobileServiceMutationHook(options?: { disableToast?: boolean, disableRefetch?: boolean }): { addMobileServiceMutation: any, loading: any, error: any } {
  // Hooks
  const { t } = useTranslation(["toasts"]);
  const { tenderID } = useParams<{ tenderID: string }>();

  const [addMobileServiceMutation, { loading, error }] = useMutation<AddMobileServiceMutation, AddMobileServiceMutationVariables>(
    AddMobileServiceDocument, {
      onCompleted(data) {
        if (!options?.disableToast) {
          toast(CustomToastComponent, {
            toastId: "mobile-service-added-success",
            data: {
              title: t("toasts.mobileserviceaddedsuccessfully"),
              type: "success"
            }
          });
        }

        // Update the cache here if needed
        updateCache(data.addMobileService as MobileService);
      },
      refetchQueries: options?.disableRefetch
        ? []
        : [
          {
            query: FetchTenderByIdDocument,
            variables: { id: Number(tenderID) }
          },
          {
            query: FetchServicesOnTenderDocument,
            variables: { tenderId: Number(tenderID) }
          }
        ]
    });

  /**
   * Adds reference of new Service to the Tender's services array.
   * @param {Service} newData - The new cost details to be added.
   */
  const updateCache = (newData: MobileService) => {
    const tenderId = newData.tenderId;
    // Get the cache reference of the new Service
    const newServiceReference = client.cache.identify({
      __typename: "MobileService",
      id: newData.id
    });

    // Update the `services` field of the Tender object
    client.cache.modify({
      id: client.cache.identify({ __typename: "Tender", id: tenderId }),
      fields: {
        services(existingServices = []) {
          if (!existingServices) {
            return [newServiceReference];
          }

          return [...existingServices, { __ref: newServiceReference }];
        }
      }
    });
  };

  return {
    addMobileServiceMutation,
    loading,
    error
  };
}
